import React, { useEffect, useMemo, useState } from "react";
import Container from "../hoc/Container";
import {
  BanknotesIcon,
  FireIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import SelectField from "../components/common/form/selectField";
import pspricesDealsService from "../service/pspricesDeals.service";
import HeartButton from "../favorites/HeartButton";
import { favoriteFromDeal } from "../favorites/favoriteKey";

const PAGE_SIZE = 12;

const OWN_SOURCES = {
  "trendy-catalog": "* Популярные игры Nintendo eShop со скидкой. Цены обновляются ежедневно.",
  "steam-store": "* Лидеры продаж Steam со скидкой. Данные магазина Steam, обновляются каждый час.",
};

function coverAspect(deal) {
  if (deal.coverSquare) return "aspect-square";
  if (deal.coverWide) return "aspect-[460/215]";
  return "aspect-[4/3]";
}

function DealImage({ deal }) {
  return (
    <div className="relative">
      <img
        src={deal.cover || "https://via.placeholder.com/320x180?text=No+Image"}
        alt={deal.name}
        onError={(event) => {
          if (deal.coverFallback && event.currentTarget.src !== deal.coverFallback) {
            event.currentTarget.src = deal.coverFallback;
          }
        }}
        className={`${coverAspect(deal)} w-full rounded object-cover transition hover:scale-105`}
      />
      {deal.discountPercent != null ? (
        <div className="absolute bottom-0 left-0 rounded-sm bg-white px-1.5 py-1 text-xs font-semibold leading-tight text-black">
          <FireIcon className="inline-block w-4 text-orange-500" />
          {deal.discountPercent}%
        </div>
      ) : null}
    </div>
  );
}

function PsPricesDealsPage({
  title,
  regions,
  platforms,
  platformQuery,
  renderButtons,
  renderTop,
  favoritePlatform,
  renderDealAction,
  getDealImageAction,
  dealImageTitle = "Купить код",
  moreActions,
  initialRegion,
  ymPath,
  matchDeal,
  filterKey,
}) {
  const [region, setRegion] = useState(
    regions.some((item) => item.value === initialRegion)
      ? initialRegion
      : regions[0]?.value,
  );
  const [deals, setDeals] = useState([]);
  const [ownSource, setOwnSource] = useState(null);
  const ownDeals = Boolean(ownSource);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const moreUrl = useMemo(
    () => pspricesDealsService.buildCollectionUrl(region, platformQuery),
    [region, platformQuery],
  );

  const visibleDeals = useMemo(
    () => (matchDeal ? deals.filter(matchDeal) : deals),
    [deals, matchDeal],
  );
  const pageCount = Math.max(1, Math.ceil(visibleDeals.length / PAGE_SIZE));
  const pageDeals = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return visibleDeals.slice(start, start + PAGE_SIZE);
  }, [visibleDeals, currentPage]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await pspricesDealsService.getDeals(region, platforms);
        if (cancelled) {
          return;
        }
        setDeals(data.results);
        setOwnSource(OWN_SOURCES[data.meta?.source] || null);
        setCurrentPage(1);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error("Error loading psprices deals:", err);
        setError("Ошибка загрузки данных о скидках");
        setDeals([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [region, platforms]);

  useEffect(() => {
    if (ymPath && window.ym) {
      window.ym(93997310, "hit", ymPath, {
        params: {
          title,
          referer: "https://trendy-eng-shop.ru/",
        },
      });
    }
  }, [ymPath, title]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterKey]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [currentPage, region]);

  const handleRegionChange = (e) => {
    setRegion(e.value);
  };

  return (
    <Container>
      <div className="content-block">
        <h1 className="head-1">{title}</h1>

        {typeof renderTop === "function" ? (
          renderTop({ region, regions, onRegionChange: setRegion, deals: loading ? [] : deals })
        ) : (
          <section className="mx-5 md:flex md:items-end md:justify-between">
            <div>
              <p className="mb-2 mt-4 text-center text-xl">Коды пополнения:</p>
              {typeof renderButtons === "function" ? renderButtons(region) : null}
            </div>
            {regions.length > 1 ? (
              <div className="dark:dark-text my-2 flex justify-center">
                <SelectField
                  label="Регион"
                  value={region}
                  onChange={handleRegionChange}
                  options={regions}
                  name="region"
                />
              </div>
            ) : null}
          </section>
        )}

        {loading ? (
          <div className="mt-8 text-center text-2xl">Загрузка скидок...</div>
        ) : error ? (
          <h3 className="mt-8 text-center text-2xl text-red-500">{error}</h3>
        ) : (
          <>
            <section className="m-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3 lg:grid-cols-4">
              {pageDeals.map((deal) => {
                const imageAction = getDealImageAction?.(deal, region);
                return (
                  <div key={deal.id}>
                    <div className="relative">
                      {imageAction ? (
                        <button
                          type="button"
                          onClick={imageAction}
                          title={dealImageTitle}
                          className="block w-full text-left"
                        >
                          <DealImage deal={deal} />
                        </button>
                      ) : (
                        <a href={deal.link} target="_blank" rel="noreferrer">
                          <DealImage deal={deal} />
                        </a>
                      )}
                      {favoritePlatform ? (
                        <HeartButton
                          item={favoriteFromDeal(favoritePlatform, deal, region)}
                          className="absolute right-1.5 top-1.5"
                        />
                      ) : null}
                    </div>
                    <div className="flex justify-between pt-2">
                      <div className="flex min-w-0 items-baseline gap-x-1.5 whitespace-nowrap text-xs leading-tight sm:text-sm">
                        <BanknotesIcon className="inline-block w-3 shrink-0 text-green-500" />
                        <span className="font-bold">{deal.price}</span>
                        {deal.originalPrice ? (
                          <span className="text-[11px] text-gray-500 line-through sm:text-xs">
                            {deal.originalPrice}
                          </span>
                        ) : null}
                        {deal.priceBadge ? (
                          <span
                            className={`min-w-0 truncate text-[10px] font-semibold ${
                              deal.priceBadge.type === "new_low"
                                ? "text-purple-600 dark:text-purple-400"
                                : "text-green-600 dark:text-green-400"
                            }`}
                          >
                            {deal.priceBadge.label}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {typeof renderDealAction === "function"
                      ? renderDealAction(deal, region)
                      : null}
                    {deal.discountEndDate ? (
                      <p className="pb-2 text-xs">
                        <ClockIcon className="mr-2 inline-block w-3.5 text-red-500" />
                        до{" "}
                        {new Date(deal.discountEndDate).toLocaleDateString("ru", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    ) : null}
                    {deal.seoPath ? (
                      <a href={deal.seoPath}>
                        <h2 className="text-sm font-light hover:underline">
                          {deal.name}
                          {deal.platforms?.some((item) => /switch\s*2/i.test(item)) ? (
                            <span className="ml-1 rounded bg-red-600 px-1 align-middle text-[10px] font-semibold text-white">
                              Switch 2
                            </span>
                          ) : null}
                        </h2>
                        <span className="text-xs text-indigo-600 dark:text-indigo-400">
                          где сейчас дешевле →
                        </span>
                      </a>
                    ) : (
                      <a href={deal.link} target="_blank" rel="noreferrer">
                        <h2 className="text-sm font-light hover:underline">
                          {deal.name}
                          {deal.platforms?.some((item) => /switch\s*2/i.test(item)) ? (
                            <span className="ml-1 rounded bg-red-600 px-1 align-middle text-[10px] font-semibold text-white">
                              Switch 2
                            </span>
                          ) : null}
                        </h2>
                      </a>
                    )}
                  </div>
                );
              })}
            </section>

            <p className="dark:dark-text mb-1 text-center text-xs">
              {visibleDeals.length
                ? `${1 + PAGE_SIZE * (currentPage - 1)} - ${Math.min(
                    PAGE_SIZE * currentPage,
                    visibleDeals.length,
                  )} из ${visibleDeals.length}`
                : "Нет скидок"}
            </p>

            <div className="mt-4 flex w-full flex-wrap justify-center gap-2">
              {Array.from({ length: pageCount }, (_, index) => index + 1).map((page) => (
                <button
                  key={`page-${page}`}
                  type="button"
                  className={currentPage === page ? "pagItemActive" : "pagItem"}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              {ownDeals ? null : (
                <a
                  href={moreUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                >
                  Больше скидок
                </a>
              )}
              {moreActions}
            </div>

            <div className="mt-10 text-center text-xs">
              {ownDeals ? (
                ownSource
              ) : (
                <>
                  * Данные по скидкам получены через API сайта{" "}
                  <a
                    href="https://psprices.com"
                    className="text-blue-700 dark:text-indigo-400"
                    target="_blank"
                    rel="noreferrer"
                  >
                    psprices.com
                  </a>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Container>
  );
}

export default PsPricesDealsPage;
